import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import {
  DEMO_COUNTDOWN_MS,
  pad,
  parseEndsAt,
  remaining,
  type Remaining,
} from '@/features/storefront/sections/countdown';
import type { CountdownSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * A ticking clock to the end of a sale.
 *
 * **Three audiences for one missing date.** The theme builder never writes an
 * end time, and a merchant adds the section before they pick one, so "no
 * `ends_at`" is the common first state. The live shop draws nothing for it - a
 * clock to nowhere is not a sale. The customiser draws a demo clock with a
 * prompt under it, so the merchant can judge the look before choosing the
 * date. A theme's static preview draws the demo clock alone, because there the
 * point is what the theme looks like with a sale on.
 *
 * **Ticking without fidgeting.** The first paint already shows the right time
 * (the clock is read when the component is made, not after an effect), the
 * digits are tabular and padded so nothing shifts as they change, and the
 * timer stops while the tab is hidden and catches up the moment it is back.
 * A screen reader hears one polite sentence a minute, not sixty a minute.
 */

export interface CountdownRenderers {
  /** A piece of the section's copy, typeable in the customiser. */
  text: (field: 'title' | 'subtitle' | 'cta_label' | 'expired_message', value: string) => ReactNode;
  /** A link that is inert in the customiser and routed on the shop. */
  link: (href: string, className: string, style: CSSProperties, children: ReactNode) => ReactNode;
}

export interface CountdownViewProps extends CountdownRenderers {
  section: CountdownSection;
  /** The customiser: a demo clock and a prompt where the date is missing. */
  preview: boolean;
  /** A theme's static preview: a demo clock where the date is missing. */
  demo: boolean;
}

/**
 * Re-read the clock on each second until `target`, paused while the tab is
 * hidden. `null` - nothing to count to - never ticks.
 */
function useNow(target: number | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target === null) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      if (Date.now() >= target) return;
      // Lands just after the next whole second, so every tick moves a digit.
      timer = setTimeout(tick, 1000 - (Date.now() % 1000) + 10);
    };
    function tick() {
      timer = undefined;
      setNow(Date.now());
      if (!document.hidden) schedule();
    }
    const onVisibility = () => {
      if (document.hidden) {
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
        return;
      }
      setNow(Date.now());
      if (timer === undefined) schedule();
    };

    // The clock may have moved since the first render read it.
    setNow(Date.now());
    if (!document.hidden) schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [target]);

  return now;
}

/** Where the merchant's section needs them - in the customiser only. */
function Hint({ children }: { children: ReactNode }) {
  return (
    <p
      className="mt-6 rounded-xl border border-dashed px-4 py-4 text-center text-sm"
      style={{ borderColor: 'var(--storefront-muted)', color: 'var(--storefront-muted)' }}
    >
      {children}
    </p>
  );
}

const UNIT_STYLE: CSSProperties = {
  // A tint of whatever the band's text is, so the boxes read on a pale page
  // and on a primary band alike.
  backgroundColor: 'color-mix(in srgb, currentColor 8%, transparent)',
  borderRadius: 'var(--storefront-radius)',
};

function Clock({
  left,
  showDays,
  compact,
  centred,
}: {
  left: Remaining;
  showDays: boolean;
  compact: boolean;
  centred: boolean;
}) {
  const copy = useShopContent();
  const units = [
    ...(showDays ? [{ key: 'days', value: left.days, label: copy('section.countdown_days') }] : []),
    { key: 'hours', value: left.hours, label: copy('section.countdown_hours') },
    { key: 'minutes', value: left.minutes, label: copy('section.countdown_minutes') },
    { key: 'seconds', value: left.seconds, label: copy('section.countdown_seconds') },
  ];

  if (compact) {
    return (
      <p aria-hidden="true" className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-lg font-semibold">
        {units.map((unit) => (
          <span key={unit.key} data-unit={unit.key} className="whitespace-nowrap">
            <span className="tabular-nums">{pad(unit.value)}</span>{' '}
            <span className="text-xs font-normal uppercase tracking-wide opacity-70">{unit.label}</span>
          </span>
        ))}
      </p>
    );
  }

  return (
    <div aria-hidden="true" className={cn('flex flex-wrap gap-2 sm:gap-3', centred && 'justify-center')}>
      {units.map((unit) => (
        <div
          key={unit.key}
          data-unit={unit.key}
          className="flex min-w-[4.25rem] flex-col items-center px-3 py-3 sm:min-w-[5rem]"
          style={UNIT_STYLE}
        >
          <span className="text-3xl leading-none tabular-nums sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
            {pad(unit.value)}
          </span>
          <span className="mt-2 text-[11px] uppercase tracking-wide opacity-70">{unit.label}</span>
        </div>
      ))}
    </div>
  );
}

export function CountdownView({ section, preview, demo, text, link }: CountdownViewProps) {
  const copy = useShopContent();
  const configured = parseEndsAt(section.ends_at);
  // Read once, so the demo clock counts down rather than standing still.
  const [demoTarget] = useState(() => Date.now() + DEMO_COUNTDOWN_MS);
  const target = configured ?? (preview || demo ? demoTarget : null);

  const now = useNow(target);

  if (target === null) return null;

  const left = remaining(target, now, section.show_days);
  const expired = left.total <= 0;

  if (expired && section.expired_behavior === 'hide') {
    return preview ? (
      <Container width="narrow">
        <Hint>This sale has ended, so the countdown is hidden on your shop. Set a new end time to show it again.</Hint>
      </Container>
    ) : null;
  }

  const inline = section.style === 'inline';
  const boxed = section.style === 'boxed';
  // Two pictures, one each side of a centred clock. Only once one of them is
  // there: a flanking countdown with neither is the plain centred band.
  const flanks = [
    { side: 'left', url: section.image_url, focal: section.image_focal },
    { side: 'right', url: section.second_image_url, focal: section.second_image_focal },
  ] as const;
  const flankCount = flanks.filter((flank) => flank.url).length;
  const flanking = !inline && section.image_layout === 'flanking' && flankCount > 0;
  const lone = flankCount === 1;
  const beside = !inline && !flanking && section.image_layout !== 'flanking' && !!section.image_url;
  const centred = !inline && !beside;

  // Minutes only, so the sentence changes - and is announced - once a minute.
  const spoken =
    left.days > 0
      ? copy('section.countdown_left', { days: left.days, hours: left.hours, minutes: left.minutes })
      : copy('section.countdown_left_hours', { hours: left.hours, minutes: left.minutes });

  const heading = section.title ? (
    <h2
      className={cn('tracking-tight', inline ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl')}
      style={STOREFRONT_HEADING_STYLE}
    >
      {text('title', section.title)}
    </h2>
  ) : null;

  const subtitle = section.subtitle ? (
    <p className={cn(inline ? 'mt-1 text-sm' : 'mt-2 text-base')} style={{ color: 'var(--storefront-muted)' }}>
      {text('subtitle', section.subtitle)}
    </p>
  ) : null;

  const cta =
    section.cta_label && !expired
      ? link(
          section.href || '/products',
          cn('inline-flex h-12 shrink-0 items-center px-7 text-sm font-semibold', !inline && 'mt-7'),
          STOREFRONT_BUTTON_STYLE,
          text('cta_label', section.cta_label),
        )
      : null;

  const body = expired ? (
    <p className={cn('text-lg font-semibold', !inline && 'mt-6')} role="status">
      {text('expired_message', section.expired_message)}
    </p>
  ) : (
    <div className={cn(!inline && 'mt-6')}>
      <Clock left={left} showDays={section.show_days} compact={inline} centred={centred} />
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {spoken}
      </span>
    </div>
  );

  const missingHint =
    preview && configured === null ? (
      <Hint>Set an end time to start this countdown. Until then it is hidden on your shop; this clock is a demo.</Hint>
    ) : null;

  if (inline) {
    return (
      <Container>
        <div
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
          data-countdown-style="inline"
        >
          <div className="min-w-0">
            {heading}
            {subtitle}
          </div>
          {body}
          {cta}
        </div>
        {missingHint}
      </Container>
    );
  }

  const copyBlock = (
    <div className={cn(centred && 'text-center')}>
      {heading}
      {subtitle}
      {body}
      {cta}
    </div>
  );

  // On a phone the words come first and the two pictures stand side by side
  // under them; from a tablet up, the clock sits between them.
  const content = flanking ? (
    <div
      data-countdown-images="flanking"
      className="grid grid-cols-2 items-center gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)] md:gap-8"
    >
      {flanks.map((flank) =>
        flank.url ? (
          <div
            key={flank.side}
            data-flank={flank.side}
            className={cn(
              'w-full overflow-hidden',
              flank.side === 'left' ? 'md:order-1' : 'md:order-3',
              // A lone picture takes the phone's whole width, at a wide shape.
              lone ? 'col-span-2 aspect-[4/3] md:col-span-1 md:aspect-[3/4]' : 'aspect-[3/4]',
            )}
            style={{ borderRadius: 'var(--storefront-radius)' }}
          >
            <StorefrontImage
              url={flank.url}
              sizes="(min-width: 768px) 25vw, 50vw"
              alt=""
              className="size-full object-cover"
              style={focalStyle(flank.focal)}
            />
          </div>
        ) : (
          // Holds the clock in the middle while one side is still empty.
          <div
            key={flank.side}
            aria-hidden="true"
            className={cn('hidden md:block', flank.side === 'left' ? 'md:order-1' : 'md:order-3')}
          />
        ),
      )}
      <div className="order-first col-span-2 md:order-2 md:col-span-1">{copyBlock}</div>
    </div>
  ) : beside ? (
    <div className="grid items-center gap-8 md:grid-cols-2">
      <div className="aspect-[4/3] w-full overflow-hidden" style={{ borderRadius: 'var(--storefront-radius)' }}>
        <StorefrontImage
          url={section.image_url}
          sizes="(min-width: 768px) 50vw, 100vw"
          alt=""
          className="size-full object-cover"
          style={focalStyle(section.image_focal)}
        />
      </div>
      {copyBlock}
    </div>
  ) : (
    copyBlock
  );

  if (boxed) {
    return (
      <Container width={beside || flanking ? 'page' : 'narrow'}>
        <div
          className="border px-6 py-8 sm:px-10 sm:py-10"
          data-countdown-style="boxed"
          style={{
            borderColor: 'color-mix(in srgb, currentColor 15%, transparent)',
            borderRadius: 'var(--storefront-radius)',
            backgroundColor: 'var(--storefront-background)',
          }}
        >
          {content}
        </div>
        {missingHint}
      </Container>
    );
  }

  return (
    <Container>
      <div data-countdown-style="banner">{content}</div>
      {missingHint}
    </Container>
  );
}
